// src/pages/lkp/LookupReferenceStep.tsx
//
// Step 2 — ingest the client's authoritative reference (OPTIONAL). This is where
// client data actually loads:
//   1. upload the client value workbook (+ optional physical→logical dictionary)
//   2. Build authoritative — matches physical columns to client logical fields/values
//   3. review & confirm/remap each mapping (LookupReferenceReview: score, runner-up,
//      observed-vs-governed comparison)
//   4. Fold into entities — attaches the confirmed client values to the entities as
//      canonical evidence, then hands off to Step 3.
// No final code-value decisions here — that is Step 3.
//
// SIMPLE-UI: ONE primary action - "Apply client values & continue" (internally: fold the confirmed
// client codes into the lookup entities, written in each field's own format). Without a workbook
// the same place offers "Skip". Re-opening the upload review and recomputing the mappings (which
// now KEEPS the SME's confirmations/rejections) are in the step's "More" menu.

import React, { useCallback, useEffect, useState } from "react";
import {
  lookup_getAuthoritative, lookup_getCandidates, lookup_getLogicalFields,
  lookup_uploadReference, lookup_uploadReferenceKind, lookup_buildAuthoritative,
  type AuthoritativeBlock, type LookupCandidatesBlock,
} from "../api/lkpAPI";
import { lookup_foldAuthoritative } from "../api/lkpEntityAPI";
import LookupReferenceReview from "./LookupReferenceReview";
import Step2ReviewModal from "./Step2ReviewModal";
import type { StepActions } from "./LookupWorkbench";

interface Props {
  appNm: string; entityId: string;
  onGoDiscover: () => void;      // back to Step 1
  onFolded: () => void;          // continue to Step 3
  onActions?: (a: StepActions) => void;   // SIMPLE-UI v2: actions shown in the workbench top bar
}

export default function LookupReferenceStep({ appNm, entityId, onGoDiscover, onFolded, onActions }: Props) {
  const [auth, setAuth] = useState<AuthoritativeBlock | null>(null);
  const [cand, setCand] = useState<LookupCandidatesBlock | null>(null);
  const [logicalFields, setLogicalFields] = useState<string[]>([]);
  const [refFile, setRefFile] = useState<File | null>(null);
  const [dictFile, setDictFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmRerun, setConfirmRerun] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 4000); };

  const load = useCallback(async () => {
    const [a, c, lf] = await Promise.allSettled([
      lookup_getAuthoritative(entityId), lookup_getCandidates(entityId), lookup_getLogicalFields(entityId),
    ]);
    setAuth(a.status === "fulfilled" ? a.value.data : null);
    setCand(c.status === "fulfilled" ? c.value.data : null);
    setLogicalFields(lf.status === "fulfilled" ? lf.value.logical_fields : []);
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  // READ-ONLY: upload the file, then open the review modal. The modal computes the diff in
  // memory (diff_reference persists nothing). We do NOT run build_authoritative here — that
  // writes lookup_authoritative to disk, which must not happen before the SME accepts anything.
  const uploadAndReview = async () => {
    setBusy("build");
    try {
      if (refFile) await lookup_uploadReference(appNm, refFile);
      if (dictFile) await lookup_uploadReferenceKind(appNm, "dictionary", dictFile);
      setUploaded(true);
      setRefFile(null); setDictFile(null);   // selection processed - the top bar returns to "Apply"
      setRefreshSignal((n) => n + 1);   // recompute the diff for the modal
      setShowReview(true);              // open the review modal (diff runs read-only inside)
      note("Uploaded. Review the changes in the popup — nothing is saved until you accept.");
    } catch (e: any) { note(e?.detail || "Upload failed", true); } finally { setBusy(null); }
  };

  // Builds the mapping proposals (writes lookup_authoritative). Only on explicit action —
  // used by "Re-run full mapping" and before folding. NOT part of Upload & review.
  const build = async () => {
    setBusy("build");
    try {
      const r = await lookup_buildAuthoritative(appNm, entityId); setAuth(r.data);
      const lf = await lookup_getLogicalFields(entityId); setLogicalFields(lf.logical_fields);
      note("Mapping proposals rebuilt.");
    } catch (e: any) { note(e?.detail || "Build failed", true); } finally { setBusy(null); }
  };

  const foldAndContinue = async () => {
    setBusy("fold");
    try {
      // ensure mapping proposals exist before folding (Upload & review no longer builds them)
      if (!auth?.definitions?.length) { const r = await lookup_buildAuthoritative(appNm, entityId); setAuth(r.data); }
      const r = await lookup_foldAuthoritative(entityId); note(`Client values applied to ${r.attached} field(s).`); onFolded();
    } catch (e: any) { note(e?.detail || "Fold failed", true); } finally { setBusy(null); }
  };

  const built = !!auth?.definitions?.length;

  // SIMPLE-UI v2: this step's actions live in the workbench top bar (nothing at the bottom).
  useEffect(() => {
    if (!onActions) return;
    const pending = !!(refFile || dictFile);   // a file is chosen but not uploaded yet
    const skip = { label: "Skip →", onClick: onFolded, disabled: !!busy || pending,
      title: pending ? "Upload & compare the chosen file first (next to the file pickers)" : "No client workbook: the lookups use the values found in the data" };
    const hint = pending ? "File chosen - press Upload & compare next to the file pickers to process it." : undefined;
    if (built) {
      onActions({
        hint,
        primary: { label: busy === "fold" ? "Applying…" : "Apply client values & continue →", onClick: foldAndContinue,
          disabled: !!busy || pending,
          title: pending ? "Upload & compare the chosen file first - otherwise the previous workbook would be applied"
                         : "Attach the accepted client codes to the lookups (in each field's own format) and continue" },
        menu: [
          { label: "Re-open the upload comparison", onClick: () => { setRefreshSignal((x) => x + 1); setShowReview(true); }, disabled: !!busy },
          { label: "Recompute mappings (keeps your decisions)", onClick: () => setConfirmRerun(true), disabled: !!busy },
        ],
      });
    } else {
      onActions({ hint, primary: skip, menu: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, busy, refFile, dictFile, auth, onActions]);

  return (
    <div className="lrs">
      <style>{CSS}</style>

      {flash && <div className={"lrs-flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}

      <div className="lrs-intro">
        <b>Optional.</b> If the client already maintains these code lists in a workbook, upload it so
        their codes become the reference. No workbook? Use <b>Skip</b> below.
      </div>

      <div className="lrs-grid">
        <div className="lrs-card">
          <div className="lrs-h">Client value workbook</div>
          <div className="lrs-d">Codes + descriptions the client governs (the LOV source).</div>
          <label className="lrs-file">{refFile ? refFile.name : "Choose workbook (.xlsx/.csv)"}
            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setRefFile(e.target.files?.[0] || null)} /></label>
        </div>
        <div className="lrs-card">
          <div className="lrs-h">Dictionary <span className="opt">optional</span></div>
          <div className="lrs-d">Physical → logical field names. Enables dictionary-first matching.</div>
          <label className="lrs-file opt">{dictFile ? dictFile.name : "Choose dictionary (.xlsx/.csv)"}
            <input type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => setDictFile(e.target.files?.[0] || null)} /></label>
        </div>
      </div>

      <div className={"lrs-bar" + ((refFile || dictFile) ? " ready" : "")}>
        <div className="lrs-bar-t"><b>Upload &amp; compare</b>
          <span>{(refFile || dictFile)
            ? <>Ready: {[refFile?.name, dictFile?.name].filter(Boolean).join(" + ")}. Compares it with what is in place - nothing is saved until you accept each change.</>
            : <>Choose a workbook (and optionally a dictionary) above. Nothing is saved until you accept each change.</>}</span></div>
        <button className="lrs-btn dark" disabled={!!busy || !(refFile || dictFile)} onClick={uploadAndReview}
          title="Process the chosen file(s) and open the comparison">
          {busy === "build" ? "Processing…" : "Upload & compare"}</button>
      </div>

      {built ? (
        <>
          <LookupReferenceReview
            entityId={entityId} auth={auth} candidates={cand} logicalFields={logicalFields}
            busy={!!busy} onSaved={load} onReconcile={foldAndContinue} onGoDiscover={onGoDiscover} hideFold />
          {showReview && (
            <Step2ReviewModal
              appNm={appNm} entityId={entityId} refreshSignal={refreshSignal}
              onClose={() => setShowReview(false)} onApplied={load} />
          )}
          {confirmRerun && (
            <div className="lrs-modal" onClick={() => setConfirmRerun(false)}>
              <div className="lrs-modalbox" onClick={(e) => e.stopPropagation()}>
                <div className="lrs-modalh">Recompute mappings?</div>
                <p>This recomputes every mapping proposal from the uploaded workbook. The mappings you
                  confirmed or rejected are KEPT; if a proposal changed since your decision, you will see
                  it marked for review. Your Step-3 lists are not affected.</p>
                <p className="lrs-modalp">Useful after a new workbook version or an application update.</p>
                <div className="lrs-modalf">
                  <button className="lrs-btn" onClick={() => setConfirmRerun(false)}>Cancel</button>
                  <button className="lrs-btn dark" onClick={() => { setConfirmRerun(false); build(); }}>Recompute</button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="lrs-empty">No client workbook loaded. Choose one above and use <b>Upload &amp; compare</b> at the
          top - or <b>Skip</b> to use the values found in the data.</div>
      )}
    </div>
  );
}

const CSS = `
.lrs-more{position:relative;margin-right:10px}
.lrs-menu{position:absolute;left:0;bottom:calc(100% + 4px);background:#fff;border:1px solid #dde3df;border-radius:9px;box-shadow:0 8px 24px rgba(20,32,43,.12);z-index:20;min-width:270px;overflow:hidden}
.lrs-menu button{display:block;width:100%;text-align:left;border:none;background:transparent;padding:9px 13px;font-size:12.5px;cursor:pointer;font-family:inherit}
.lrs-menu button:hover{background:#f7f9f7}
.lrs-reviewbar{display:flex;align-items:center;gap:12px;margin-top:12px;padding:12px 14px;background:#f6f9fb;border:1px solid #e3ebf0;border-radius:10px;font-size:13px;color:#5f6e7d}
.lrs-reviewbar span{flex:1}

.lrs-adv{display:flex;align-items:center;gap:10px;margin-top:16px;padding-top:12px;border-top:1px dashed #e3ebf0}
.lrs-advbtn{all:unset;cursor:pointer;font-size:12px;color:#8a6d3b;background:#fcf6e9;border:1px solid #ecdca8;border-radius:20px;padding:5px 12px}
.lrs-advnote{font-size:11.5px;color:#a0a8b0}
.lrs-modal{position:fixed;inset:0;background:rgba(14,36,54,.5);display:grid;place-items:center;z-index:80;padding:20px}
.lrs-modalbox{background:#fff;border-radius:12px;max-width:460px;padding:20px;box-shadow:0 20px 60px rgba(0,0,0,.3)}
.lrs-modalh{font-size:16px;font-weight:600;color:#b23a30;margin-bottom:10px}
.lrs-modalbox p{font-size:13px;color:#33404e;line-height:1.55;margin:0 0 8px}
.lrs-modalp{color:#8a6d3b !important;font-weight:500}
.lrs-modalf{display:flex;gap:10px;justify-content:flex-end;margin-top:14px}

.lrs{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--line2:#e9ede9;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--warn:#c23c28;--warn-bg:#f7e0da;--infer:#4a51c0;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx);font-size:14px}
.lrs *{box-sizing:border-box}
.lrs-flash{padding:9px 14px;border-radius:8px;font-size:13px;margin-bottom:12px;background:var(--gov-bg);color:var(--gov)}
.lrs-flash.err{background:var(--warn-bg);color:var(--warn)}
.lrs-intro{font-size:13px;color:var(--tx2);background:var(--surface2);border:1px solid var(--line);border-radius:10px;padding:11px 14px;margin-bottom:14px}
.lrs-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px}
.lrs-card{background:var(--surface);border:1px solid var(--line);border-radius:11px;padding:14px 15px}
.lrs-h{font-weight:600;font-size:13.5px}.lrs-h .opt{font-size:10.5px;color:var(--tx3);font-weight:500;margin-left:6px;text-transform:uppercase;letter-spacing:.05em}
.lrs-d{font-size:12px;color:var(--tx2);margin:3px 0 11px}
.lrs-file{display:block;border:1px dashed var(--line);border-radius:8px;padding:10px 12px;font-size:12.5px;color:var(--infer);cursor:pointer;text-align:center;background:var(--surface2)}
.lrs-file:hover{border-color:var(--infer)}.lrs-file.opt{color:var(--tx3)}
.lrs-bar{display:flex;align-items:center;gap:14px;background:var(--surface);border:1px solid var(--line);border-radius:11px;padding:13px 15px;margin-bottom:14px}
.lrs-bar.ready{border-color:#0e8a6e;background:#f2faf6}
.lrs-bar-t{flex:1}.lrs-bar-t b{font-size:13.5px;display:block}.lrs-bar-t span{font-size:12.5px;color:var(--tx2)}
.lrs-btn{border:1px solid var(--line);background:var(--surface);border-radius:8px;padding:9px 15px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.lrs-btn.dark{background:var(--ink);color:#fff;border-color:var(--ink)}.lrs-btn.dark:hover{background:#20303e}
.lrs-btn:disabled{opacity:.5;cursor:default}
.lrs-foot{display:flex;align-items:center;gap:11px;margin-top:16px;padding-top:14px;border-top:1px dashed var(--line);font-size:12.5px;color:var(--tx2);flex-wrap:wrap}
.lrs-sp{flex:1}
.lrs-empty{text-align:center;padding:34px;color:var(--tx3)}
@media(max-width:820px){.lrs-grid{grid-template-columns:1fr}}
`;
